import React, {useState} from 'react'
import {Button, Icon, Modal} from '@therms/atalaya'
import {pluralize} from '@therms/web-js'
import {Alerts, RPCDocs, useRPCContext} from '@therms/react-modules'
import {InProgressVisitRunListItem} from '@src/modules/Patrol/router/screens/components/PatrolRouteRunModal/components/VisitsInProgressWarningModal/components/InProgressVisitRunListItem'
import {MdCheckCircle, MdFlaky} from 'react-icons/md'
import VisitElement = RPCDocs.Patrol.GetPatrolRouteRunDetails.v2.Visit

interface VisitsInProgressWarningModalProps {
  closeHandler: () => void
  inProgressVisits: VisitElement[]
  onVisitUpdateComplete?: () => void
  patrolRouteEndHandler: () => void
  visitsCount: number
  visitsDoneCount: number
}

function VisitsInProgressWarningModal({
  closeHandler,
  inProgressVisits,
  onVisitUpdateComplete,
  patrolRouteEndHandler,
  visitsCount,
  visitsDoneCount,
}: VisitsInProgressWarningModalProps) {
  const {rpcService} = useRPCContext()
  const [loading, setLoading] = useState(false)
  const incompleteVisits = visitsCount - visitsDoneCount

  const handleVisitUpdate = async (
    args: RPCDocs.Patrol.UpdatePatrolRouteRunVisit.v1.Args,
  ) => {
    try {
      setLoading(true)
      await rpcService.Patrol.UpdatePatrolRouteRunVisit.v1.call(args)
      Alerts.success('Visit Updated')
      onVisitUpdateComplete?.()
    } catch (err: any) {
      Alerts.error('There was a problem saving your visit.', err.message)

      console.log('handleVisitSave() err', err)
    } finally {
      setLoading(false)
    }
  }

  const handlePatrolRouteEnd = () => {
    patrolRouteEndHandler()
    closeHandler()
  }

  if (inProgressVisits.length) {
    return (
      <Modal closeHandler={closeHandler} header="End Route Confirmation">
        <div className="px-sm">
          <div className="text-color-strong text-lg font-medium">
            <span className="">{inProgressVisits.length}</span>{' '}
            <span className="">
              {pluralize('Visit', inProgressVisits.length)} still In Progress
            </span>
          </div>

          <span>
            Please resolve the following visits before ending the Patrol Run.
          </span>
        </div>

        <div className="space-y-xxs mt-sm">
          {inProgressVisits.map((locationVisit) => (
            <div
              className="p-sm bg-surface-subtle rounded"
              key={locationVisit.id}
            >
              <InProgressVisitRunListItem
                arrivalTime={locationVisit.arrivalTime}
                departureTime={locationVisit.departureTime}
                lengthMin={locationVisit.visit.lengthMin}
                locationId={locationVisit.visit.locationId}
                onSubmit={handleVisitUpdate}
                runVisitId={locationVisit.id}
                visitNotes={locationVisit.visit.notes}
                visitRunNotes={locationVisit.notes}
              />
            </div>
          ))}
        </div>
      </Modal>
    )
  }

  if (incompleteVisits) {
    return (
      <Modal
        closeHandler={closeHandler}
        escPressClose
        header="End Route Confirmation"
        outsideClickClose
      >
        <div className="p-base text-center">
          <Icon Icon={MdFlaky} color="caution" size="xl" />

          <div className="text-color-strong text-lg font-medium">
            <span>{incompleteVisits}</span>{' '}
            <span>{pluralize('Visit', incompleteVisits)} incomplete</span>
          </div>

          <div> Are you sure you want to end the current Patrol Run?</div>

          <Button
            className="mt-base"
            loading={loading}
            onClick={handlePatrolRouteEnd}
            variant="caution"
          >
            End Patrol Run
          </Button>
        </div>
      </Modal>
    )
  }

  return (
    <Modal
      closeHandler={closeHandler}
      escPressClose
      header="End Route Confirmation"
    >
      <div className="p-base text-center">
        <Icon Icon={MdCheckCircle} color="positive" size="xl" />

        <div className="text-color-strong mt-xxs text-lg font-medium">
          All Visits Completed
        </div>

        <Button
          className="mt-lg"
          loading={loading}
          onClick={handlePatrolRouteEnd}
          variant="main"
        >
          End Patrol Route
        </Button>
      </div>
    </Modal>
  )
}

export {VisitsInProgressWarningModal}
